/**
 * travelErrorMessages — DER EINE UI-Mapper von Reisekosten-Fehler → übersetzter Text
 * (RK-18, E17/V23, architecture-reisekosten.md §14.3).
 *
 * Bis RK-18 landeten rohe, technische Fehler-Strings (inkl. Paragraphen-Zitaten und
 * Feldpfaden wie „mileage.routeNote darf nicht leer sein (…§8.2 Recht-Recherche)") direkt im
 * UI. Diese Datei ist der EINZIGE erlaubte Übersetzungsweg (V23) — `TravelExpensesPage`
 * ruft `formatTravelError` an ALLEN 7 Fehler-Sites auf, NIE `err.message` direkt.
 *
 * Der Roh-Fehler (inkl. `TravelValidationIssue.detail`) geht IMMER zusätzlich an
 * `logger.error` — NIE in den zurückgegebenen, gerenderten String.
 *
 * @module travelErrorMessages
 */

import type { TFunction } from 'i18next';
import { TravelApiError } from '../../api/travelApiError';
import logger from '../logger';

/**
 * Übersetzt einen `travelApi`/`travelValidation`-Fehler in einen nutzerfreundlichen,
 * übersetzten Text (Wording-Gate V15 — keine Paragraphen-Zitate, kein Feldpfad-Jargon in den
 * `travel.json`-Texten selbst).
 *
 * Mapping (architecture-reisekosten.md §14.3):
 * - `TravelApiError{code:'validationFailed', issues}` → `t('validation.<issues[0].code>')`,
 *   bei mehr als einem Issue zusätzlich `t('validation.moreIssues', {count})` angehängt
 *   (`count` = Anzahl der WEITEREN Issues, also `issues.length - 1`).
 * - `TravelApiError{code:'calcFailed', calcError}` → `t('settlement.errors.<calcError>')`
 *   (bestehendes, bereits übersetztes Fehlercode-Vokabular des Berechnungskerns).
 * - Jeder andere `TravelApiError`-Code → `t('apiErrors.<code>')`.
 * - Unbekannter/nicht-typisierter Fehler (kein `TravelApiError`) → `t('apiErrors.unknown')`.
 *
 * @param err - Der gefangene Fehler (`unknown` — aus einem `catch`-Block).
 * @param t - i18next-Übersetzungsfunktion des `'travel'`-Namespace.
 * @returns Ein übersetzter, render-sicherer String — NIE der Roh-Fehler/das technische Detail.
 */
export function formatTravelError(err: unknown, t: TFunction): string {
  if (err instanceof TravelApiError) {
    // Der Roh-Fehler (message + issues/calcError) geht NUR in den Logger — NIE in den
    // Rückgabe-String (V23).
    logger.error('travelApi: TravelApiError (raw, log-only — never rendered, V23)', err);

    if (err.code === 'validationFailed' && err.issues && err.issues.length > 0) {
      const firstIssueMessage = t(`validation.${err.issues[0].code}`);
      const remainingCount = err.issues.length - 1;
      if (remainingCount > 0) {
        return `${firstIssueMessage} ${t('validation.moreIssues', { count: remainingCount })}`;
      }
      return firstIssueMessage;
    }

    if (err.code === 'calcFailed' && err.calcError) {
      return t(`settlement.errors.${err.calcError}`);
    }

    return t(`apiErrors.${err.code}`);
  }

  // Nicht-typisierter Fehler (z. B. ein Netzwerkfehler außerhalb von travelApi) — ebenfalls
  // NUR loggen, NIE roh rendern (V23).
  logger.error('travelApi: unbekannter, nicht-typisierter Fehler (raw, log-only, V23)', err);
  return t('apiErrors.unknown');
}
